import { useEffect, useMemo } from 'react';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import { AlertCircle, Loader2 } from 'lucide-react';

import { Button } from '@/components/atoms/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/atoms/card';
import { PublicLayout } from '@/components/public/PublicLayout';

type PaymentLinkPayload = {
  tenantId?: string;
  invoiceId?: string;
  amount?: number;
  ts?: number;
};

export const Route = createFileRoute('/(public)/pay/$token')({
  component: PayLinkPage,
});

function decodePaymentToken(token: string): PaymentLinkPayload | null {
  try {
    const normalized = token.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
    const decoded = window.atob(padded);
    return JSON.parse(decoded) as PaymentLinkPayload;
  } catch {
    return null;
  }
}

function PayLinkPage() {
  const router = useRouter();
  const { token } = Route.useParams();

  const payload = useMemo(() => decodePaymentToken(token), [token]);
  const invoiceId = payload?.invoiceId ?? null;

  useEffect(() => {
    if (!invoiceId) return;
    void router.navigate({
      to: '/invoice/$id',
      params: { id: invoiceId },
      replace: true,
    });
  }, [invoiceId, router]);

  if (!invoiceId) {
    return (
      <PublicLayout>
        <div className="flex justify-center py-20">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-amber-500" />
                Invalid payment link
              </CardTitle>
              <CardDescription>This payment link is malformed or no longer usable.</CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => window.location.assign('/')}
              >
                Back to home
              </Button>
            </CardContent>
          </Card>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <div className="flex justify-center py-20">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          Redirecting to invoice…
        </div>
      </div>
    </PublicLayout>
  );
}
